import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  FileText, MessageSquare, Scale, Briefcase, Building2, 
  Shield, Lightbulb, Users, Calculator, PenTool, Brain,
  BarChart3, BookOpen, Zap, Clock, Home, Gavel, FileCheck,
  Lock, BookMarked, ArrowRight, HeartHandshake, GraduationCap
} from 'lucide-react';

const HUB_ICONS = {
  documentos_personales: FileText,
  consultas_legales: MessageSquare,
  mis_causas: Scale,
  contratos_simples: Briefcase,
  constitucion_empresas: Building2,
  contratos_comerciales: FileText,
  compliance: Shield,
  propiedad_intelectual: Lightbulb,
  laboral_empresa: Users,
  tributario: Calculator,
  escritos_judiciales: PenTool,
  gestion_causas: Briefcase,
  investigacion_juridica: BookOpen,
  calculadoras: Calculator,
  firma_digital: PenTool,
  ai_estrategia: Brain,
  dashboard_firma: BarChart3,
  gestion_clientes: Users,
  equipo_trabajo: Users,
  biblioteca_firma: BookOpen,
  litigios_masivos: Zap,
  business_intelligence: BarChart3,
  escrituras_publicas: FileText,
  repertorio: BookMarked,
  certificaciones: FileCheck,
  verificacion_identidad: Lock,
  inmobiliario: Home,
  sucesiones: FileText,
  poderes: Gavel,
  derecho_familia: HeartHandshake,
  agentes_especialistas: GraduationCap
};

const HUB_ROUTES = {
  documentos_personales: '/escritos-legales',
  consultas_legales: '/consejero-legal',
  mis_causas: '/poder-judicial',
  contratos_simples: '/crear-contrato',
  constitucion_empresas: '/constituir-empresa',
  contratos_comerciales: '/crear-contrato',
  compliance: '/proteger-contrato',
  propiedad_intelectual: '/templates',
  laboral_empresa: '/calculadora-laboral',
  tributario: '/contabilidad',
  escritos_judiciales: '/escritos-legales',
  gestion_causas: '/poder-judicial',
  investigacion_juridica: '/jurisprudencia',
  calculadoras: '/calculadora-laboral',
  firma_digital: '/firma-electronica',
  ai_estrategia: '/inteligencia-judicial',
  dashboard_firma: '/admin',
  gestion_clientes: '/crm',
  equipo_trabajo: '/admin',
  biblioteca_firma: '/jurisprudencia',
  litigios_masivos: '/poder-judicial',
  business_intelligence: '/admin',
  escrituras_publicas: '/templates',
  repertorio: '/crm',
  certificaciones: '/verificar',
  verificacion_identidad: '/verificar',
  inmobiliario: '/crear-contrato',
  sucesiones: '/consejero-legal',
  poderes: '/templates',
  derecho_familia: '/hub-familia',
  agentes_especialistas: '/agentes-especialistas'
};

const HUB_COLORS = {
  documentos_personales: 'from-blue-500 to-blue-600',
  consultas_legales: 'from-purple-500 to-purple-600',
  mis_causas: 'from-amber-500 to-amber-600',
  contratos_simples: 'from-emerald-500 to-emerald-600',
  constitucion_empresas: 'from-indigo-500 to-indigo-600',
  contratos_comerciales: 'from-cyan-500 to-cyan-600',
  compliance: 'from-rose-500 to-rose-600',
  propiedad_intelectual: 'from-yellow-500 to-yellow-600',
  laboral_empresa: 'from-teal-500 to-teal-600',
  tributario: 'from-orange-500 to-orange-600',
  escritos_judiciales: 'from-violet-500 to-violet-600',
  gestion_causas: 'from-sky-500 to-sky-600',
  investigacion_juridica: 'from-fuchsia-500 to-fuchsia-600',
  calculadoras: 'from-lime-500 to-lime-600',
  firma_digital: 'from-pink-500 to-pink-600',
  ai_estrategia: 'from-red-500 to-red-600',
  dashboard_firma: 'from-slate-500 to-slate-600',
  gestion_clientes: 'from-green-500 to-green-600',
  equipo_trabajo: 'from-blue-400 to-blue-500',
  biblioteca_firma: 'from-amber-400 to-amber-500',
  litigios_masivos: 'from-red-400 to-red-500',
  business_intelligence: 'from-purple-400 to-purple-500',
  escrituras_publicas: 'from-emerald-400 to-emerald-500',
  repertorio: 'from-indigo-400 to-indigo-500',
  certificaciones: 'from-cyan-400 to-cyan-500',
  verificacion_identidad: 'from-rose-400 to-rose-500',
  inmobiliario: 'from-teal-400 to-teal-500',
  sucesiones: 'from-orange-400 to-orange-500',
  poderes: 'from-violet-400 to-violet-500',
  derecho_familia: 'from-rose-500 to-pink-600',
  agentes_especialistas: 'from-indigo-500 to-blue-600'
};

const HubCard = ({ hub, onClick, size = 'normal' }) => {
  const IconComponent = HUB_ICONS[hub.id] || FileText;
  const colorClass = HUB_COLORS[hub.id] || 'from-gray-500 to-gray-600';
  
  if (size === 'small') {
    return (
      <button
        onClick={() => onClick(hub)}
        className="w-full p-3 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg border border-gray-700 text-left transition-all hover:border-blue-500/50 group"
        data-testid={`hub-card-${hub.id}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 bg-gradient-to-r ${colorClass} rounded-lg flex items-center justify-center`}>
            <IconComponent className="w-4 h-4 text-white" />
          </div>
          <span className="text-white text-sm font-medium">{hub.name}</span>
        </div>
      </button>
    );
  }
  
  return (
    <button
      onClick={() => onClick(hub)}
      className="p-5 bg-gray-800/50 hover:bg-gray-700/50 rounded-xl border border-gray-700 text-left transition-all hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/10 group"
      data-testid={`hub-card-${hub.id}`}
    >
      <div className={`w-12 h-12 bg-gradient-to-r ${colorClass} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
        <IconComponent className="w-6 h-6 text-white" />
      </div>
      <h3 className="text-white font-semibold mb-1">{hub.name}</h3>
      <p className="text-gray-400 text-sm line-clamp-2">{hub.description}</p>
      
      {hub.features && hub.features.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {hub.features.slice(0, 3).map((feature, idx) => (
            <span key={idx} className="text-xs bg-gray-700/50 text-gray-300 px-2 py-0.5 rounded">
              {feature.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}
      
      <div className="mt-4 flex items-center text-blue-400 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
        <span>Acceder</span>
        <ArrowRight className="w-4 h-4 ml-1" />
      </div>
    </button>
  );
};

const HubDashboard = ({ profileType, onHubSelect }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [hubs, setHubs] = useState([]);
  const [profileInfo, setProfileInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profileType) {
      loadHubs();
    }
  }, [profileType]);

  const loadHubs = async () => {
    try {
      const response = await api.get(`/platform/profiles/${profileType}`);
      setProfileInfo(response.data);
      setHubs(response.data.hubs || []);
    } catch (error) {
      console.error('Error loading hubs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleHubClick = (hub) => {
    if (onHubSelect) {
      onHubSelect(hub);
    }
    const route = HUB_ROUTES[hub.id] || '/dashboard';
    navigate(route);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="animate-pulse bg-gray-800 rounded-xl h-40"></div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {profileInfo && (
        <div className="mb-6 flex items-center gap-3">
          <span className="text-3xl">{profileInfo.icon}</span>
          <div>
            <h2 className="text-xl font-bold text-white">{profileInfo.name}</h2>
            <p className="text-gray-400 text-sm">{profileInfo.description}</p>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {hubs.map((hub) => (
          <HubCard key={hub.id} hub={hub} onClick={handleHubClick} />
        ))}
      </div>
    </div>
  );
};

export { HubCard, HubDashboard, HUB_ROUTES };
export default HubDashboard;
